import { useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/useAuth';
import { moduleRegistry } from '@/config/module-registry.config';
import { quickLinks } from '@/config/quick-links.config';
import { filterModulesByRole } from '@/lib/rbac';
import LucideIcon from '@/components/LucideIcon';
import { Home, ShieldCheck } from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarHeader,
  SidebarRail,
} from '@/components/ui/sidebar';

/**
 * Application sidebar — reads module entries from the registry,
 * filters by the authenticated user's role, and renders nav items.
 *
 * Uses shadcn/ui Sidebar with `collapsible="icon"` so it collapses
 * to icon-only on desktop. On mobile the Sidebar component automatically
 * renders as a sheet overlay.
 */
export default function AppSidebar() {
  const { role } = useAuth();
  const { pathname } = useLocation();

  const modules = filterModulesByRole(moduleRegistry, role);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              render={<Link to="/" />}
              isActive={pathname === '/'}
              tooltip="Dashboard"
            >
              <Home className="size-4" />
              <span>Dashboard</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Modules</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {modules.map((mod) => (
                <SidebarMenuItem key={mod.id}>
                  <SidebarMenuButton
                    render={<Link to={mod.routePath} />}
                    isActive={pathname === mod.routePath}
                    tooltip={mod.name}
                  >
                    <LucideIcon name={mod.icon} className="size-4" />
                    <span>{mod.name}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {quickLinks.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>Quick Links</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {quickLinks.map((link) => (
                  <SidebarMenuItem key={link.id}>
                    <SidebarMenuButton
                      render={
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        />
                      }
                      tooltip={link.name}
                    >
                      <LucideIcon name={link.icon} className="size-4" />
                      <span>{link.name}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {role === 'admin' && (
          <SidebarGroup>
            <SidebarGroupLabel>Admin</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    render={<Link to="/admin" />}
                    isActive={pathname.startsWith('/admin')}
                    tooltip="Admin Console"
                  >
                    <ShieldCheck className="size-4" />
                    <span>Admin Console</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarRail />
    </Sidebar>
  );
}
